import React, { useEffect, useState } from "react";
import { LayoutGrid, List } from "lucide-react";

// Small segmented control that flips between compact list rows and
// card-with-thumbnail views. Persists per-collection via localStorage
// so ops's preference sticks across sessions and reloads.
//
// Design principle: cards are richer but heavier — every visible card
// downloads its thumbnail from Storage. List view skips <img> entirely
// so no image bytes are fetched. Default is "list" for that reason.

const STORAGE_PREFIX = "cms.viewMode.";

/**
 * Read-write hook keyed by a stable collection identifier.
 *
 *   const [viewMode, setViewMode] = useViewMode("blogs", "list");
 *
 * Returns "list" | "card". Persists to localStorage on set.
 */
export function useViewMode(collectionKey, defaultMode = "list") {
  const key = collectionKey ? `${STORAGE_PREFIX}${collectionKey}` : null;

  const [mode, setModeState] = useState(() => {
    if (!key || typeof window === "undefined") return defaultMode;
    try {
      const stored = window.localStorage.getItem(key);
      return stored === "card" || stored === "list" ? stored : defaultMode;
    } catch {
      return defaultMode;
    }
  });

  useEffect(() => {
    if (!key || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, mode);
    } catch {
      /* localStorage may be disabled — silent no-op */
    }
  }, [key, mode]);

  const setMode = (next) => {
    if (next !== "list" && next !== "card") return;
    setModeState(next);
  };

  return [mode, setMode];
}

/**
 * Compact segmented control. Renders two icon buttons — a list icon
 * and a grid icon — with the active one filled. Keep it small so it
 * fits comfortably next to search inputs and filter chips.
 */
export default function ViewModeToggle({ value, onChange, className = "" }) {
  const options = [
    { value: "list", label: "List view", Icon: List },
    { value: "card", label: "Card view", Icon: LayoutGrid },
  ];
  return (
    <div
      className={`inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-gray-100 ${className}`}
      role="group"
      aria-label="View mode"
    >
      {options.map(({ value: v, label, Icon }) => {
        const active = value === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            aria-pressed={active}
            title={label}
            className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
              active
                ? "bg-white text-leechiuBlue shadow-sm ring-1 ring-gray-200"
                : "text-gray-500 hover:text-leechiuBlue"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        );
      })}
    </div>
  );
}
